import {
  ApplicationStatus,
  DataVendors,
  VerificationStatus
} from "shared/types";
import { CreditReport, VerificationReport } from "types/landlord/reports";
import { CreditScore } from "./components/CreditScore";
import { PersonalData } from "./components/PersonalData";
import { BankruptcyDetails } from "./components/BankruptcyDetails";
import { ConsumerInformation } from "./components/ConsumerInformation";
import { ConsumerStatement } from "./components/ConsumerStatement";
import { TrdPartyCollectionDetails } from "./components/TrdPartyCollectionDetails";
import { HistoricalConsumerInformation } from "./components/HistoricalConsumerInformation";
import { AlertsAndTriggers } from "./components/AlertsAndTriggers";
import { AccountsOverview } from "./components/AccountsOverview";
import { EmploymentInformation } from "./components/EmploymentInformation";
import { CreditInquiries } from "./components/CreditInquiries";
import { CreditReportSummary } from "./components/CreditReportSummary";
import { AccountsSummary } from "./components/AccountsSummary";
import { ReportException } from "views/applications-id-report/components/ReportException";
import { PrintReady } from "views/applications-id-report/providers/PrintProvider";
import { PotentialNegativeInfo } from "./components/PotentialNegativeInfo";
import { CollectionsSummary } from "views/applications-id-report/slices/CreditReportSlice/components/CollectionsSummary";
import { errorHitCodes } from "./utils";
import { ApplicantSettings, VerificationNames } from "shared/types";
import { getFullName } from "shared/utils/get-full-name";
import { Applicant } from "types/application";
import { CustomerInquiry } from "./components/CustomerInquiry";
import { RerunModal } from "../../components/RerunModal";
import { Warnings } from "./components/Warnings";
import { has } from "shared/utils";
import { Loader } from "shared/components/Loader";
import { getPastDueAccountMetricValue } from "shared/utils/past-due-account-metrics";

type CreditReportSliceProps = {
  creditReport?: VerificationReport<CreditReport>;
  isFetched?: boolean;
  applicant: Applicant;
  verificationId: number;
  activeReportId?: number;
  pastDueAccountType: string;
  screeningCriteria?: ApplicantSettings;
  applicationStatus: ApplicationStatus;
  openRerunModal: boolean;
  setOpenRerunModal: (openRerunModal: boolean) => void;
  isSIN?: boolean;
};

const CreditReportSlice = ({
  creditReport,
  isFetched,
  applicant,
  verificationId,
  activeReportId,
  pastDueAccountType,
  screeningCriteria,
  applicationStatus,
  openRerunModal,
  setOpenRerunModal,
  isSIN
}: CreditReportSliceProps) => {
  const { report } = creditReport ?? {};

  const isInsufficientData = report?.score == 4;
  const isNoHit =
    (report?.hit_code?.code == "2" &&
      report.hit_code.description == "No-Hit") ||
    report?.hit_code?.code === "N";

  const {
    personal_data,
    consumer_information,
    account_overview,
    foreclosure_accounts,
    alerts_and_triggers,
    credit_inquiries,
    historical_consumer_information,
    employment_information,
    bankruptcy_details,
    bankruptcy,
    consumer_statements,
    collections,
    past_due_account_metrics,
    balance_past_due,
    balance_of_collections,
    debt_balance,
    criteria,
    request_data,
    ssn_mismatched,
    itin_detected,
    customer_reference_number
  } = report ?? {};

  const bankruptcyYears = Number(
    applicant.template_criteria_settings?.bankruptcy_records?.years
  );
  const bankruptcyCriteriaValue =
    applicant.criteria_check_settings?.bankruptcy_records?.value;

  const foreclosureAccountsCount =
    foreclosure_accounts?.length ?? criteria?.foreclosure_accounts ?? 0;

  // TransUnion Canada reports a bankruptcy count instead of a detail list.
  const bankruptciesCount = bankruptcy_details?.length ?? bankruptcy ?? 0;

  if (!isFetched) return <Loader />;

  if (
    Object.keys(report || {}).length === 0 ||
    errorHitCodes.includes(report?.hit_code?.code || "") ||
    creditReport?.status === VerificationStatus.Failed
  )
    return (
      <>
        <ReportException
          applicationStatus={applicationStatus}
          applicationId={applicant.application_id}
          verificationId={verificationId}
          hitCode={report?.hit_code}
          onRerunReport={() => setOpenRerunModal(true)}
          rerunRequirements={creditReport?.rerun_requirements}
        />
        {creditReport ? (
          <RerunModal
            open={openRerunModal}
            onClose={() => setOpenRerunModal(false)}
            reportFee={creditReport.fee}
            applicantId={applicant.id}
            applicationId={applicant.application_id}
            verificationId={verificationId}
            verificationName={VerificationNames.CreditReport}
          />
        ) : null}
      </>
    );

  const isPastDuePercentage = pastDueAccountType === "Percentage";
  const pastDueAccount = getPastDueAccountMetricValue({
    metrics: past_due_account_metrics,
    isPercentage: isPastDuePercentage
  });

  return (
    <>
      {has(activeReportId) && <PrintReady verificationId={activeReportId} />}

      <CreditReportSummary
        creditScore={report?.score}
        bankruptcy={bankruptciesCount}
        bankruptcyYears={bankruptcyYears}
        bankruptcyCriteriaValue={String(bankruptcyCriteriaValue)}
        pastDueAccount={pastDueAccount}
        balanceOfUnpaidCollections={balance_past_due}
        balanceOfCollections={balance_of_collections}
        foreclosureAccounts={foreclosureAccountsCount}
        criteria={criteria}
        verificationId={verificationId}
        completed_at={creditReport?.completed_at}
        isNoHit={isNoHit}
        isInsufficientData={isInsufficientData}
        debtBalance={debt_balance}
        applicantName={getFullName({
          firstName: applicant.first_name,
          lastName: applicant.last_name,
          middleName: applicant.middle_name,
          short: true
        })}
        pastDueAccountType={pastDueAccountType}
        screeningCriteria={screeningCriteria}
        vendor={creditReport?.vendor as DataVendors}
      />
      <Warnings
        applicationId={String(applicant.application_id)}
        applicantId={String(applicant.id)}
        verificationId={String(verificationId)}
        consumer_information={consumer_information}
        alerts_and_triggers={alerts_and_triggers}
        ssn_mismatched={ssn_mismatched}
        itin_detected={itin_detected}
        provided_ssn={request_data?.ssn}
        equifax_ssn={personal_data?.ssn}
        provided_address={
          request_data
            ? `${request_data.house_number} ${request_data.street_name}, ${request_data.city}, ${request_data.state} ${request_data.zip}`
            : undefined
        }
        equifax_address={
          consumer_information
            ? `${consumer_information.address1 || ""}, ${consumer_information.city || ""}, ${consumer_information.state || ""} ${consumer_information.zip || ""}`.trim()
            : undefined
        }
        vendor={creditReport?.vendor as DataVendors}
      />
      {!isSIN && (
        <div id="customer-inquiry">
          <CustomerInquiry
            applicationId={String(applicant.application_id)}
            applicantId={String(applicant.id)}
            verificationId={String(verificationId)}
            requestData={request_data}
            customerReferenceNumber={customer_reference_number}
            firstName={applicant.first_name}
            lastName={applicant.last_name}
          />
        </div>
      )}
      <div id="personal-data">
        <PersonalData
          applicationId={String(applicant.application_id)}
          applicantId={String(applicant.id)}
          verificationId={String(verificationId)}
          personal_data={personal_data}
          isNoHit={isNoHit}
          providedSSN={request_data?.ssn}
          ssnMismatched={ssn_mismatched}
          isSIN={isSIN}
        />
      </div>
      <div id="consumer-information">
        <ConsumerInformation
          consumer_information={consumer_information}
          isNoHit={isNoHit}
        />
      </div>
      <CreditScore
        score={Number(report?.score)}
        isNoHit={isNoHit}
        isInsufficientData={isInsufficientData}
        model_number={
          report?.request_data?.model_number ??
          report?.model_number ??
          undefined
        }
        score_reason={report?.score_reason}
      />
      <AccountsSummary accounts={account_overview} isNoHit={isNoHit} />
      <PotentialNegativeInfo
        accounts={account_overview}
        isNoHit={isNoHit}
        bankruptciesNumber={bankruptciesCount}
        collectionsNumber={collections?.length}
      />
      <BankruptcyDetails bankrupcies={bankruptcy_details} isNoHit={isNoHit} />
      {/* No data yet */}
      {/* <TradesSummaryAndAccountStatus /> */}
      <AccountsOverview
        account_overview={account_overview}
        isNoHit={isNoHit}
        verificationId={verificationId}
        vendor={creditReport?.vendor as DataVendors}
      />
      <EmploymentInformation
        employment_information={employment_information}
        isNoHit={isNoHit}
      />
      <div id="alerts-and-triggers">
        <AlertsAndTriggers
          alerts_and_triggers={alerts_and_triggers}
          isNoHit={isNoHit}
        />
      </div>
      <ConsumerStatement
        consumer_statements={consumer_statements}
        isNoHit={isNoHit}
      />
      <CollectionsSummary isNoHit={isNoHit} collections={collections} />
      <TrdPartyCollectionDetails
        collections={collections}
        isNoHit={isNoHit}
        vendor={creditReport?.vendor as DataVendors}
      />
      <CreditInquiries credit_inquiries={credit_inquiries} isNoHit={isNoHit} />
      <HistoricalConsumerInformation
        historical_consumer_information={historical_consumer_information}
        isNoHit={isNoHit}
      />
      {creditReport ? (
        <RerunModal
          reportFee={creditReport.fee}
          open={openRerunModal}
          onClose={() => setOpenRerunModal(false)}
          applicantId={applicant.id}
          applicationId={applicant.application_id}
          verificationId={verificationId}
          verificationName={VerificationNames.CreditReport}
        />
      ) : null}
    </>
  );
};

export default CreditReportSlice;
